<template>
  <v-container>
    <v-data-table
      fixed-header
      :header-props="{ sortIcon: 'fas fa-sort-amount-up-alt' }"
      :headers="headers"
      :items="items"
      :loading="loading"
      sort-by="updated_at"
      :sort-desc="true"
      :items-per-page="itemsPerPage"
      @update:items-per-page="updateRecordsPerPage"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 50, 100],
        showFirstLastPage: true
      }"
      :options.sync="options"
      :server-items-length="totalItems"
      :dense="showDense"
      class="elevation-2"
    >
      <template v-slot:top>
        <v-row align="center" class="py-1 px-2" no-gutters>
          <v-col cols="12" sm="6">
            <v-breadcrumbs
              class="px-2"
              :items="breadcrumbsItems"
            ></v-breadcrumbs>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            :class="
              $store.getters.LANGUAGE == 'fa' ? 'text-left' : 'text-right'
            "
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  @click.stop="getReports"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>fa-sync</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("refresh") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  @click="showDense = !showDense"
                  v-bind="attrs"
                  v-on="on"
                  :color="showDense ? 'success' : ''"
                >
                  <v-icon small>fas fa-compress-alt</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("compactView") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  v-bind="attrs"
                  v-on="on"
                  color="orange"
                  @click="helpDialog = true"
                >
                  <v-icon small>fa-question</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("showHelp") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.index="{ item }">
        {{
          (options.page - 1) * options.itemsPerPage + items.indexOf(item) + 1
        }}
      </template>
      <template v-slot:item.print_page_number="{ item }">
        <span v-if="item.print_page_number">{{
          $vuetify.lang.translator("pageNumber")
        }}</span>
        <br v-if="item.print_page_number && item.high_quality" />
        <span v-if="item.high_quality">{{
          $vuetify.lang.translator("highQuality")
        }}</span>
      </template>
      <template v-slot:item.updated_at="{ item }">
        <div class="ltr-text" v-if="$store.getters.LANGUAGE == 'fa'">
          {{ item.updated_at | moment("jYYYY/jM/jD HH:mm") }}
        </div>
        <div class="ltr-text" v-else>
          {{ item.updated_at | moment("YYYY/M/D HH:mm") }}
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              :to="{
                name: 'AdminReportOnMap',
                params: { id: item.id, name: item.title }
              }"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>fa-map</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("reportOnMap") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              @click="showPDFFiles(item.id, item.title)"
              :disabled="item.status !== 7"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>fa-file-pdf</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("reportInPDF") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              @click="downloadExcel(item.id)"
              :disabled="item.status !== 7 || excelID == item.id"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <span v-if="excelID == item.id"
                >{{ downloadExcelPercentage }}%</span
              >
              <v-icon small v-else>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("reportInExcel") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              @click="downloadKMZ(item.id)"
              :disabled="item.status !== 7 || kmzID == item.id"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <span v-if="kmzID == item.id">{{ downloadKmzPercentage }}%</span>
              <v-icon small v-else>fa-globe</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("reportInKMZ") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              :to="{
                name: 'AdminReportLogs',
                params: { id: item.id, name: item.title }
              }"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>fa-history</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("viewOperations") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              @click.stop="openStatus(item.id, item.status, item.title)"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>far fa-hourglass</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("changeStatus") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="error"
              @click.stop="confirmDelete(item.id, item.title)"
              :disabled="
                item.status == 2 ||
                  item.status == 3 ||
                  item.status == 4 ||
                  item.status == 5
              "
              v-bind="attrs"
              v-on="on"
              v-if="showDelete"
            >
              <v-icon small>fa-trash</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("delete") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="deleteDialog"
      max-width="640px"
      persistent
      @keydown.esc="closeDelete"
    >
      <v-card>
        <v-card-title class="headline">{{ dialogTitle }}</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closeDelete">{{
            $vuetify.lang.translator("no")
          }}</v-btn>
          <v-btn
            color="error"
            @click="tryDelete"
            :disabled="waitForDelete"
            :loading="waitForDelete"
            >{{ $vuetify.lang.translator("yes") }}</v-btn
          >
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="statusDialog"
      persistent
      max-width="640"
      @keydown.esc="statusDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="statusDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <v-timeline dense>
            <v-timeline-item
              icon="fa-edit"
              fill-dot
              :color="selectedReportStatus === 1 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("dataEntry") }}
            </v-timeline-item>
            <v-timeline-item
              icon="fa-hourglass-start"
              fill-dot
              :color="selectedReportStatus === 2 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("waitingToCreate") }}
            </v-timeline-item>
            <v-timeline-item
              icon="fa-file-excel"
              fill-dot
              :color="selectedReportStatus === 3 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("creatingExcel") }}</v-timeline-item
            >
            <v-timeline-item
              icon="fa-file-pdf"
              fill-dot
              :color="selectedReportStatus === 4 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("creatingPDF") }}</v-timeline-item
            >
            <v-timeline-item
              icon="fa-globe"
              fill-dot
              :color="selectedReportStatus === 5 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("creatingKMZ") }}</v-timeline-item
            >
            <v-timeline-item
              icon="fa-bug"
              fill-dot
              :color="selectedReportStatus === 6 ? 'green' : 'blue'"
            >
              {{ $vuetify.lang.translator("error") }}
            </v-timeline-item>
            <v-timeline-item
              icon="fa-hourglass-end"
              fill-dot
              :color="selectedReportStatus === 7 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("finished") }}
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            :disabled="
              selectedReportStatus == 2 ||
                selectedReportStatus == 3 ||
                selectedReportStatus == 4 ||
                selectedReportStatus == 5
            "
            @click="changeStatus()"
            :loading="waitForStatus"
            block
            >{{ $vuetify.lang.translator("changeStatusToStart") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="pdfFilesDialog"
      persistent
      max-width="640"
      @keydown.esc="pdfFilesDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{ pdfFilesDialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="pdfFilesDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-data-table
          fixed-header
          :header-props="{ sortIcon: 'fas fa-sort-amount-up-alt' }"
          :headers="pdfFilesHeaders"
          :items="pdfFilesItems"
          :loading="waitforPDFFiles"
          :dense="showDense"
          class="elevation-2"
        >
          <template v-slot:item.created_at="{ item }">
            <div class="ltr-text" v-if="$store.getters.LANGUAGE == 'fa'">
              {{ item.created_at | moment("jYYYY/jM/jD HH:mm") }}
            </div>
            <div class="ltr-text" v-else>
              {{ item.created_at | moment("YYYY/M/D HH:mm") }}
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="downloadPDF(item.report_id, item.name)"
                  :disabled="pdfName == item.name"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span v-if="pdfName == item.name"
                    >{{ downloadPdfPercentage }}%</span
                  >
                  <v-icon small v-else>fa-download</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("download") }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="helpDialog"
      persistent
      max-width="800"
      @keydown.esc="helpDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{
            $vuetify.lang.translator("help")
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="helpDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div
            class="mt-6"
            v-html="$vuetify.lang.translator('adminReportsHelp')"
          ></div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="success" @click="helpDialog = false">{{
            $vuetify.lang.translator("gotIt")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { handleError } from "@/helpers/helper";

export default {
  name: "AdminReports",
  data: () => ({
    breadcrumbsItems: [],
    headers: [],
    items: [],
    loading: true,
    itemsPerPage: 0,
    options: {},
    totalItems: 0,
    showDense: true,
    showDelete: false,
    deleteDialog: false,
    dialogTitle: "",
    waitForDelete: false,
    helpDialog: false,
    excelID: 0,
    pdfName: "",
    kmzID: 0,
    downloadPdfPercentage: 0,
    downloadExcelPercentage: 0,
    downloadKmzPercentage: 0,
    showStatus: false,
    statusDialog: false,
    selectedReport: 0,
    selectedReportStatus: 0,
    waitForStatus: false,
    pdfFilesHeaders: [],
    pdfFilesItems: [],
    pdfFilesDialog: false,
    pdfFilesDialogTitle: "",
    waitforPDFFiles: false
  }),
  watch: {
    options: {
      handler() {
        this.getReports();
      }
    },
    showDense: function() {
      this.$store.dispatch("saveShowDense", this.showDense ? "show" : "hide");
    }
  },
  created() {
    this.itemsPerPage = parseInt(this.$store.getters.PERPAGE);
    this.showDense = this.$store.getters.SHOWDENSE === "show";

    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("dashboard"),
      to: { name: "Dashboard" }
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("management")
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("reports"),
      to: { name: "AdminReports" }
    });

    this.headers.push({
      text: this.$vuetify.lang.translator("row"),
      value: "index",
      sortable: false,
      align: "center"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("user"),
      value: "user_name",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("contractor"),
      value: "contractor_name",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("employer"),
      value: "employer_name",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("title"),
      value: "title",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("specifications"),
      value: "print_page_number",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("status"),
      value: "status_title",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("updateDate"),
      value: "updated_at"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("operations"),
      value: "actions",
      sortable: false,
      align: "center"
    });

    this.pdfFilesHeaders.push({
      text: this.$vuetify.lang.translator("name"),
      value: "name",
      sortable: false,
      align: "center"
    });
    this.pdfFilesHeaders.push({
      text: this.$vuetify.lang.translator("fileSize"),
      value: "size",
      sortable: false
    });
    this.pdfFilesHeaders.push({
      text: this.$vuetify.lang.translator("createDate"),
      value: "created_at",
      sortable: false
    });
    this.pdfFilesHeaders.push({
      text: this.$vuetify.lang.translator("operations"),
      value: "actions",
      sortable: false,
      align: "center"
    });

    this.checkPrivileges();
  },
  methods: {
    checkPrivileges() {
      let userType = parseInt(this.$store.getters.USERTYPE);
      let allowed_privileges = [1, 2];
      if (allowed_privileges.indexOf(userType) === -1) {
        this.$router.push({ name: "Error403" });
      }
      this.showDelete = true;
    },
    getReports() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      var parameters = null;
      if (sortBy[0]) {
        let sort_by = sortBy[0];
        if (sort_by === "status_title") {
          sort_by = "status";
        }
        parameters = {
          page: page,
          size: itemsPerPage,
          sort: (sortDesc[0] ? "-" : "") + sort_by
        };
      } else {
        parameters = {
          page: page,
          size: itemsPerPage
        };
      }
      var self = this;
      this.$http
        .get("/report/", { params: parameters })
        .then(function(response) {
          self.items = response.data.results;
          self.totalItems = response.data.count;
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.loading = false;
        });
    },
    confirmDelete(id, name) {
      this.idToBeDeleted = id;
      this.dialogTitle = this.$vuetify.lang.translator(
        "sureToDeleteReport",
        name
      );
      this.deleteDialog = true;
    },
    tryDelete() {
      if (this.idToBeDeleted === 0) {
        this.$toasted.error(this.$vuetify.lang.translator("selectAReport"), {
          duration: 5000,
          keepOnHover: true,
          className:
            self.$store.getters.LANGUAGE == "fa"
              ? "vazir-font-fd"
              : "vazir-font",
          iconPack: "fontawesome",
          icon: "exclamation-triangle",
          position: "top-center"
        });
        return;
      }
      this.waitForDelete = true;
      let self = this;
      self.$http
        .delete("/report/" + this.idToBeDeleted + "/")
        .then(() => {
          self.$toasted.success(
            self.$vuetify.lang.translator("reportDeleted"),
            {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "check",
              position: "top-center"
            }
          );
          self.getReports();
          self.deleteDialog = false;
        })
        .catch(error => {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.waitForDelete = false;
        });
    },
    closeDelete() {
      this.idToBeDeleted = 0;
      this.deleteDialog = false;
    },
    downloadExcel(id) {
      this.excelID = id;
      this.downloadExcelPercentage = 0;
      let self = this;
      this.$http
        .get("/report/" + id + "/excel/", {
          responseType: "blob",
          onDownloadProgress: function(event) {
            self.downloadExcelPercentage = Math.round(
              (event.loaded * 100) / event.total
            );
          }
        })
        .then(response => {
          var filename = "export";
          var disposition = response.headers["content-disposition"];
          console.log(response.headers);
          if (disposition) {
            filename = disposition
              .split(";")[1]
              .split("=")[1]
              .replace('"', "")
              .replace('"', "");
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.ms-excel"
          });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
          self.$toasted.success(
            self.$vuetify.lang.translator("downloadStarted"),
            {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "check",
              position: "top-center"
            }
          );
        })
        .catch(error => {
          if (
            error.response &&
            /json$/gi.test(error.response.headers["content-type"]) &&
            error.response.status === 400
          ) {
            self.$toasted.error(self.$vuetify.lang.translator("fileNotExist"), {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          } else {
            let result = handleError(error, self.$toasted, self.$router);
            for (var item in result) {
              self.$toasted.error(result[item], {
                duration: 5000,
                keepOnHover: true,
                className:
                  self.$store.getters.LANGUAGE == "fa"
                    ? "vazir-font-fd"
                    : "vazir-font",
                iconPack: "fontawesome",
                icon: "exclamation-triangle",
                position: "top-center"
              });
            }
          }
        })
        .then(function() {
          self.excelID = 0;
        });
    },
    showPDFFiles(id, name) {
      this.pdfFilesItems = [];
      this.pdfFilesDialogTitle = this.$vuetify.lang.translator(
        "reportPDFFiles",
        name
      );
      this.pdfFilesDialog = true;
      this.waitforPDFFiles = true;
      var self = this;
      this.$http
        .get("/report/" + id + "/pdf-files/")
        .then(function(response) {
          self.pdfFilesItems = response.data;
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.waitforPDFFiles = false;
        });
    },
    downloadPDF(reportID, fileName) {
      this.pdfName = fileName;
      this.downloadPdfPercentage = 0;
      let self = this;
      this.$http
        .get("/report/" + reportID + "/pdf/" + fileName + "/", {
          responseType: "blob",
          onDownloadProgress: function(event) {
            self.downloadPdfPercentage = Math.round(
              (event.loaded * 100) / event.total
            );
          }
        })
        .then(response => {
          var filename = "export";
          var disposition = response.headers["content-disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
            var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            var matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, "");
            }
          }
          const blob = new Blob([response.data], {
            type: "application/pdf"
          });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
          self.$toasted.success(
            self.$vuetify.lang.translator("downloadStarted"),
            {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "check",
              position: "top-center"
            }
          );
        })
        .catch(error => {
          if (
            error.response &&
            /json$/gi.test(error.response.headers["content-type"]) &&
            error.response.status === 400
          ) {
            self.$toasted.error(self.$vuetify.lang.translator("fileNotExist"), {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          } else {
            let result = handleError(error, self.$toasted, self.$router);
            for (var item in result) {
              self.$toasted.error(result[item], {
                duration: 5000,
                keepOnHover: true,
                className:
                  self.$store.getters.LANGUAGE == "fa"
                    ? "vazir-font-fd"
                    : "vazir-font",
                iconPack: "fontawesome",
                icon: "exclamation-triangle",
                position: "top-center"
              });
            }
          }
        })
        .then(function() {
          self.pdfName = "";
        });
    },
    downloadKMZ(id) {
      this.kmzID = id;
      this.downloadKmzPercentage = 0;
      let self = this;
      this.$http
        .get("/report/" + id + "/kmz/", {
          responseType: "blob",
          onDownloadProgress: function(event) {
            self.downloadKmzPercentage = Math.round(
              (event.loaded * 100) / event.total
            );
          }
        })
        .then(response => {
          var filename = "export";
          var disposition = response.headers["content-disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
            var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            var matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, "");
            }
          }
          const blob = new Blob([response.data], {
            type: "application/vnd.google-earth.kmz"
          });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
          self.$toasted.success(
            self.$vuetify.lang.translator("downloadStarted"),
            {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "check",
              position: "top-center"
            }
          );
        })
        .catch(error => {
          if (
            error.response &&
            /json$/gi.test(error.response.headers["content-type"]) &&
            error.response.status === 400
          ) {
            self.$toasted.error(self.$vuetify.lang.translator("fileNotExist"), {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          } else {
            let result = handleError(error, self.$toasted, self.$router);
            for (var item in result) {
              self.$toasted.error(result[item], {
                duration: 5000,
                keepOnHover: true,
                className:
                  self.$store.getters.LANGUAGE == "fa"
                    ? "vazir-font-fd"
                    : "vazir-font",
                iconPack: "fontawesome",
                icon: "exclamation-triangle",
                position: "top-center"
              });
            }
          }
        })
        .then(function() {
          self.kmzID = 0;
        });
    },
    openStatus(id, status, name) {
      this.selectedReport = id;
      this.selectedReportStatus = status;
      this.dialogTitle = this.$vuetify.lang.translator(
        "changeReportStatus",
        name
      );
      this.statusDialog = true;
    },
    changeStatus() {
      this.waitForStatus = true;
      var self = this;
      this.$http
        .put("/report/" + this.selectedReport + "/status/")
        .then(function() {
          self.$toasted.success(
            self.$vuetify.lang.translator("reportStatusChanged"),
            {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "check",
              position: "top-center"
            }
          );
          self.statusDialog = false;
          self.getReports();
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.waitForStatus = false;
        });
    },
    updateRecordsPerPage(rowsPerPage) {
      this.$store.dispatch("saveRecordsPerPage", rowsPerPage);
    }
  }
};
</script>
